import * as React from "react";
import { observer } from "mobx-react";
import { withStores } from "stores";

import { UitkCard } from "@egds/react-core/cards";
import { UitkSpacing } from "@egds/react-core/spacing";
import { UitkText } from "@egds/react-core/text";
import {
  UitkTable,
  UitkTableHead,
  UitkTableBody,
  UitkTableRow,
  UitkTableCell,
  UitkTableCaption,
} from "@egds/react-core/tables";

import { useLocalization } from "@shared-ui/localization-context";

import { FlightsStructuredTableProps } from "components/flexComponents/FlightsStructuredTable/typings";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";

import {
  Airport,
  FlightsStructuredTableFlexModuleResult,
} from "typings/microserviceModels/flights-structured-table-flex-module";
import { locKeys } from "../l10n";

interface AirportItemProps {
  airport: Airport;
  modulename: string;
}

export const AirportItem: React.FC<AirportItemProps> = ({ airport, modulename }) => {
  const { airportCode, country, link, count } = airport;
  const { url, name } = link;

  return (
    <UitkTableRow>
      <UitkTableCell>
        {url ? (
          <TrackedLink moduleName={modulename} action={Action.CLICK} href={url} rfrr="airportsperairline.code.link">
            {airportCode}
          </TrackedLink>
        ) : (
          airportCode
        )}
      </UitkTableCell>
      <UitkTableCell>{country}</UitkTableCell>
      <UitkTableCell>
        {url ? (
          <TrackedLink moduleName={modulename} action={Action.CLICK} href={url} rfrr="airportsperairline.name.link">
            {name}
          </TrackedLink>
        ) : (
          name
        )}
      </UitkTableCell>
      <UitkTableCell>{count}</UitkTableCell>
    </UitkTableRow>
  );
};

export const PopularOriginAirportsPerAirline = withStores("flexModuleModelStore")(
  observer((props: FlightsStructuredTableProps) => {
    const { templateComponent, flexModuleModelStore } = props;
    const {
      type,
      metadata: { id },
    } = templateComponent;

    const model = flexModuleModelStore.getModel(id) as FlightsStructuredTableFlexModuleResult | null;

    if (!model) {
      return null;
    }

    const { formatText } = useLocalization();
    const { airports } = model;

    if (!airports) {
      return null;
    }

    const { flightCountTitle, teaser, airportCode, country, airportName, flightCount } = locKeys;
    const tableHeadings = [
      formatText(airportCode),
      formatText(country),
      formatText(airportName),
      formatText(flightCount),
    ];

    return (
      <div>
        <div className="PopularOriginAirportsPerAirline" id={id}>
          <UitkSpacing padding={{ block: "three" }} margin={{ block: "three" }}>
            <UitkCard>
              <div>
                <UitkTable striped="odd">
                  <TableCaption>{formatText(flightCountTitle, model.airCarrierName)}</TableCaption>
                  <TableCaption>{formatText(teaser, model.airCarrierName)}</TableCaption>
                  <UitkTableHead>
                    <UitkTableRow>
                      {tableHeadings.map((tableHeading) => (
                        <TableHeading key={tableHeading}>{tableHeading}</TableHeading>
                      ))}
                    </UitkTableRow>
                  </UitkTableHead>
                  <UitkTableBody>
                    {airports.map((airport: Airport) => {
                      return <AirportItem airport={airport} key={airport.airportCode} modulename={type || ""} />;
                    })}
                  </UitkTableBody>
                </UitkTable>
              </div>
            </UitkCard>
          </UitkSpacing>
        </div>
      </div>
    );
  })
);

const TableHeading: React.FC = ({ children }) => (
  <UitkTableCell>
    <UitkText size={300}>{children}</UitkText>
  </UitkTableCell>
);

const TableCaption: React.FC = ({ children }) => (
  <UitkTableCaption>
    <UitkText size={400} weight="bold" theme="emphasis">
      {children}
    </UitkText>
  </UitkTableCaption>
);

export default PopularOriginAirportsPerAirline;
